// src/components/layout/SyncIndicator.tsx
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/useAuthStore';

const C = {
  accent:  '#4f8cf7',
  success: '#34d399',
  warn:    '#fbbf24',
  muted:   '#8b9cc4',
  border:  'rgba(255,255,255,0.06)',
};

interface PillState { color: string; dot: string; label: string; glow: boolean; }

export function SyncIndicator() {
  const user = useAuthStore((s) => s.user);
  const { isSyncing, lastSync, pendingItems } = useAuthStore((s) => s.syncStatus);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => forceTick((t) => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!user) return null;

  let state: PillState;
  if (isSyncing) {
    state = { color: C.accent,  dot: C.accent,  label: 'Syncing', glow: true };
  } else if (pendingItems > 0) {
    state = { color: C.warn,    dot: C.warn,    label: `${pendingItems} pending`, glow: false };
  } else if (lastSync && Date.now() - lastSync < 60_000) {
    state = { color: C.success, dot: C.success, label: 'Synced',  glow: false };
  } else {
    state = { color: C.muted,   dot: C.muted,   label: 'Online',  glow: false };
  }

  const tooltip =
    isSyncing        ? 'Syncing your progress…' :
    pendingItems > 0 ? `${pendingItems} item${pendingItems === 1 ? '' : 's'} waiting to sync` :
    lastSync         ? `Last synced ${formatTimeAgo(lastSync)}`
                     : 'Connected';

  return (
    <div
      style={{
        display:      'inline-flex',
        alignItems:   'center',
        gap:          '6px',
        padding:      '5px 12px',
        background:   'rgba(255,255,255,0.03)',
        border:       `1px solid ${C.border}`,
        borderRadius: '999px',
        fontSize:     '0.68rem',
        fontWeight:   700,
        letterSpacing:'0.06em',
        textTransform:'uppercase',
        color:        state.color,
        cursor:       'default',
        whiteSpace:   'nowrap',
      }}
      title={tooltip}
      aria-label={tooltip}
    >
      <span
        aria-hidden="true"
        style={{
          width:        '6px',
          height:       '6px',
          borderRadius: '50%',
          background:   state.dot,
          boxShadow:    state.glow ? `0 0 8px ${state.dot}` : 'none',
          flexShrink:   0,
          transition:   'box-shadow 0.3s ease',
        }}
      />
      <span>{state.label}</span>
    </div>
  );
}

function formatTimeAgo(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
